/**
 * CustomToolbarActions
 *
 * A reusable block for the right-side toolbar that lets an expert add their own
 * action buttons (their own tools / websites). Renders:
 *   - a "+" button (add)
 *   - a scrollable vertical list of the user's saved action buttons
 *   - an add/edit modal (portal'd to <body>)
 *
 * Action types:
 *   - 'link' : an http(s) URL opened in a new tab (favicon auto-fetched)
 *   - 'app'  : a URI-scheme launcher (zoommtg://, slack://, mailto:, tel: ...) that
 *              hands off to a locally-installed app if the OS has a handler. A browser
 *              cannot launch a raw .exe, so this is the only web-sanctioned "open app".
 *
 * Icons use the platform's modern line-icon set (react-icons / Bootstrap Icons),
 * stored by a stable key. 'image' (uploaded) and legacy 'emoji' are still rendered.
 *
 * Persisted per-expert on the server via /toolbar-actions (single source of truth).
 * Drop this inside any toolbar container; it manages its own state + data fetching.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import {
  BsLink45Deg, BsGlobe, BsFileEarmarkText, BsBarChartLine, BsGraphUp, BsClipboardData,
  BsKanban, BsCalendar3, BsChatDots, BsEnvelope, BsTelephone, BsFolder2Open, BsBriefcase,
  BsBank, BsTools, BsCameraVideo, BsDisplay, BsCloud, BsSearch, BsBell, BsStar, BsLock,
  BsCreditCard, BsRobot, BsPeople, BsGear, BsMusicNoteBeamed, BsSend, BsController,
  BsSlack, BsWhatsapp, BsTelegram, BsDiscord, BsSpotify, BsMicrosoftTeams,
} from 'react-icons/bs';

// ============================================================
// STYLED COMPONENTS (match the existing toolbar aesthetic)
// ============================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Divider = styled.div`
  width: 32px;
  height: 1px;
  background: rgba(99, 102, 241, 0.25);
  margin: 2px 0;
`;

// Plain vertical list. Scrolling (when there are many actions) is handled by the
// whole toolbar container in RightToolbar — NOT a nested scrollbar around these
// buttons — so nothing here clips the hover menus.
const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 2px;
`;

const SquareButton = styled.button`
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  background: rgba(20, 25, 40, 0.95);
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
  padding: 0;
  color: rgba(255, 255, 255, 0.92);

  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(99, 102, 241, 0.1);
    transform: scale(1.05);
  }

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
    border-radius: 6px;
  }

  svg.glyph {
    width: 25px;
    height: 25px;
  }

  span.emoji {
    font-size: 26px;
    line-height: 1;
  }
`;

const AddButton = styled(SquareButton)`
  border-style: dashed;
  color: rgba(147, 160, 255, 0.9);
  font-size: 26px;
  font-weight: 300;
`;

const Tooltip = styled.div`
  position: absolute;
  right: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: rgba(20, 25, 40, 0.98);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 10;

  ${SquareButton}:hover & {
    opacity: 1;
  }
`;

// Small "⋯" control revealed on hover for edit/delete/reorder
const ActionWrap = styled.div`
  position: relative;
  &:hover .kebab { opacity: 1; }
`;

const Kebab = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid rgba(99, 102, 241, 0.4);
  background: rgba(15, 20, 35, 0.98);
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  /* Faintly visible by default so it is discoverable; solid on hover. */
  opacity: 0.55;
  transition: opacity 0.2s ease, background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  &:hover { background: rgba(99, 102, 241, 0.3); opacity: 1; }
`;

// Portalled to <body> with fixed positioning so the outer toolbar's scroll
// (overflow) can never clip it — this is what previously hid the Delete option.
const Menu = styled.div`
  position: fixed;
  background: rgba(20, 25, 40, 0.99);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 2100;
  animation: ${fadeIn} 0.12s ease;
  min-width: 140px;
`;

const MenuItem = styled.button`
  text-align: left;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: rgba(99, 102, 241, 0.18); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
  &.danger { color: #ff8080; }
  &.danger:hover { background: rgba(239, 68, 68, 0.18); }
`;

// ------- Modal -------

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(3, 6, 15, 0.6);
  backdrop-filter: blur(3px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const Modal = styled.div`
  width: 440px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: linear-gradient(180deg, #141a2e 0%, #0d1220 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 22px;
  color: rgba(255, 255, 255, 0.92);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);

  h3 { margin: 0 0 4px; font-size: 18px; font-weight: 600; }
  p.sub { margin: 0 0 18px; font-size: 12.5px; color: rgba(255,255,255,0.55); }
  label { display: block; font-size: 12.5px; font-weight: 600; margin: 14px 0 6px; color: rgba(255,255,255,0.75); }
`;

const TypeToggle = styled.div`
  display: flex;
  gap: 8px;
`;

const TypeOption = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid ${p => p.$active ? 'rgba(99,102,241,0.7)' : 'rgba(99,102,241,0.2)'};
  background: ${p => p.$active ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.03)'};
  color: rgba(255,255,255,0.9);
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  &:hover { border-color: rgba(99,102,241,0.5); }
  small { display: block; font-size: 10.5px; color: rgba(255,255,255,0.5); margin-top: 2px; }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(99,102,241,0.25);
  background: rgba(255,255,255,0.04);
  color: #fff;
  font-size: 13.5px;
  outline: none;
  &:focus { border-color: rgba(99,102,241,0.6); }
  &::placeholder { color: rgba(255,255,255,0.35); }
`;

const Hint = styled.div`
  font-size: 11.5px;
  color: rgba(255,255,255,0.45);
  margin-top: 5px;
  line-height: 1.4;
`;

const IconRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const IconChoice = styled.button`
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.$active ? 'rgba(99,102,241,0.7)' : 'rgba(99,102,241,0.2)'};
  background: ${p => p.$active ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.03)'};
  color: rgba(255,255,255,0.85);
  font-size: 12.5px;
  cursor: pointer;
  &:hover { border-color: rgba(99,102,241,0.5); }
`;

const IconPicker = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const AppGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 4px;
`;

const AppChoice = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 11px;
  border-radius: 10px;
  border: 1px solid ${p => p.$active ? 'rgba(99,102,241,0.7)' : 'rgba(99,102,241,0.2)'};
  background: ${p => p.$active ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.03)'};
  color: rgba(255,255,255,0.9);
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  &:hover { border-color: rgba(99,102,241,0.5); }
  svg.ico { width: 17px; height: 17px; flex-shrink: 0; }
`;

const IconBtn = styled.button`
  width: 36px; height: 36px;
  border-radius: 9px;
  border: 1px solid ${p => p.$active ? 'rgba(99,102,241,0.75)' : 'rgba(99,102,241,0.2)'};
  background: ${p => p.$active ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.03)'};
  color: ${p => p.$active ? '#fff' : 'rgba(255,255,255,0.8)'};
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(99,102,241,0.55); color: #fff; }
  svg { width: 19px; height: 19px; }
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
`;

const PreviewBtn = styled.div`
  width: 52px; height: 52px; border-radius: 14px;
  border: 1px solid rgba(99,102,241,0.3);
  background: rgba(20,25,40,0.95);
  color: rgba(255,255,255,0.92);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  img { width: 28px; height: 28px; object-fit: contain; border-radius: 6px; }
  svg.glyph { width: 25px; height: 25px; }
  span { font-size: 26px; line-height: 1; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
`;

const Btn = styled.button`
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CancelBtn = styled(Btn)`
  background: transparent;
  border-color: rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.75);
  &:hover:not(:disabled) { background: rgba(255,255,255,0.06); }
`;

const SaveBtn = styled(Btn)`
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: #fff;
  &:hover:not(:disabled) { filter: brightness(1.1); }
`;

const ErrorText = styled.div`
  color: #ff8080;
  font-size: 12.5px;
  margin-top: 12px;
`;

// ============================================================
// HELPERS
// ============================================================

// Modern line-icon set (react-icons / Bootstrap Icons) — matches the platform's
// icon language. Actions store a stable KEY; the component is looked up at render.
const ICONS = {
  link: BsLink45Deg, globe: BsGlobe, file: BsFileEarmarkText, chart: BsBarChartLine,
  graph: BsGraphUp, report: BsClipboardData, board: BsKanban, calendar: BsCalendar3,
  chat: BsChatDots, mail: BsEnvelope, phone: BsTelephone, folder: BsFolder2Open,
  briefcase: BsBriefcase, bank: BsBank, tools: BsTools, video: BsCameraVideo,
  display: BsDisplay, cloud: BsCloud, search: BsSearch, bell: BsBell, star: BsStar,
  lock: BsLock, card: BsCreditCard, robot: BsRobot, people: BsPeople, gear: BsGear,
  music: BsMusicNoteBeamed, send: BsSend, controller: BsController,
  slack: BsSlack, whatsapp: BsWhatsapp, telegram: BsTelegram, discord: BsDiscord,
  spotify: BsSpotify, teams: BsMicrosoftTeams,
};

// The curated set shown in the icon picker (generic/tool icons first).
const ICON_PRESETS = [
  'link', 'globe', 'file', 'chart', 'graph', 'report', 'board', 'calendar', 'chat',
  'mail', 'phone', 'folder', 'briefcase', 'bank', 'tools', 'video', 'display', 'cloud',
  'search', 'bell', 'star', 'lock', 'card', 'robot', 'people', 'gear', 'music',
];

// Desktop apps whose URI scheme really does "just open the app" when installed.
// NOTE: Office apps (Word/Excel/PowerPoint/Outlook) are intentionally NOT here —
// their ms-office: scheme requires a document argument and throws an Office error
// if invoked bare, so they can't be launched blank from a link. A browser also
// CANNOT launch a raw .exe or read a file path; a registered scheme is the only
// web-sanctioned way to open an installed app.
const APP_PRESETS = [
  { name: 'Zoom', url: 'zoommtg://zoom.us', icon: 'video' },
  { name: 'Microsoft Teams', url: 'msteams://', icon: 'teams' },
  { name: 'Slack', url: 'slack://open', icon: 'slack' },
  { name: 'WhatsApp', url: 'whatsapp://', icon: 'whatsapp' },
  { name: 'Telegram', url: 'tg://', icon: 'telegram' },
  { name: 'Discord', url: 'discord://', icon: 'discord' },
  { name: 'Spotify', url: 'spotify://', icon: 'spotify' },
  { name: 'Email (default mail app)', url: 'mailto:', icon: 'mail' },
];

// Same favicon service the server falls back to — keeps preview consistent
function faviconFor(url) {
  try { return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=64`; }
  catch { return null; }
}

function renderIcon(action) {
  if (action.iconType === 'icon') {
    const Comp = ICONS[action.iconKey] || ICONS.link;
    return <Comp className="glyph" />;
  }
  if (action.iconType === 'emoji') return <span className="emoji">{action.emoji || '🔗'}</span>; // legacy
  const src = action.iconUrl || (action.type === 'link' ? faviconFor(action.url) : null);
  if (src) return <img src={src} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
  const Fallback = ICONS.link;
  return <Fallback className="glyph" />;
}

function launch(action) {
  if (!action?.url) return;
  if (action.type === 'app') {
    // URI-scheme hand-off to a local app (does nothing if no OS handler registered)
    window.location.href = action.url;
  } else {
    window.open(action.url, '_blank', 'noopener,noreferrer');
  }
}

const EMPTY_FORM = { type: 'link', label: '', url: '', iconType: 'favicon', iconUrl: '', iconKey: 'link' };

// ============================================================
// COMPONENT
// ============================================================

const CustomToolbarActions = () => {
  const api = useAuthenticatedAxios();
  const [actions, setActions] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // action being edited, or null for "add"
  const [form, setForm] = useState(EMPTY_FORM);
  const [iconFile, setIconFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/toolbar-actions');
      setActions(res.data?.actions || []);
    } catch (err) {
      console.error('[CustomToolbarActions] load failed:', err);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Close the per-button menu on any outside click
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [openMenuId]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIconFile(null);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (action) => {
    setEditing(action);
    setForm({
      type: action.type,
      label: action.label,
      url: action.url,
      iconType: action.iconType,
      iconUrl: action.iconUrl || '',
      iconKey: action.iconKey || 'link',
      emoji: action.emoji || '🔗', // legacy passthrough for old actions
    });
    setIconFile(null);
    setError('');
    setOpenMenuId(null);
    setModalOpen(true);
  };

  const remove = async (action) => {
    setOpenMenuId(null);
    setActions(prev => prev.filter(a => a.id !== action.id)); // optimistic
    try {
      await api.delete(`/toolbar-actions/${action.id}`);
    } catch (err) {
      console.error('[CustomToolbarActions] delete failed:', err);
      load(); // revert to server truth
    }
  };

  const move = async (action, dir) => {
    setOpenMenuId(null);
    const idx = actions.findIndex(a => a.id === action.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= actions.length) return;
    const next = actions.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setActions(next); // optimistic
    try {
      await api.put('/toolbar-actions/reorder', { orderedIds: next.map(a => a.id) });
    } catch (err) {
      console.error('[CustomToolbarActions] reorder failed:', err);
      load();
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setIconFile(f);
    setForm(prev => ({ ...prev, iconType: 'image', iconUrl: URL.createObjectURL(f) }));
  };

  const canSave = form.label.trim() && form.url.trim() && !saving;

  const save = async () => {
    setError('');
    if (!canSave) return;
    setSaving(true);
    try {
      let iconUrl = form.iconType === 'image' ? form.iconUrl : '';

      // Upload a freshly-picked image first to get a permanent URL
      if (form.iconType === 'image' && iconFile) {
        const fd = new FormData();
        fd.append('icon', iconFile);
        const up = await api.post('/toolbar-actions/upload-icon', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        iconUrl = up.data?.url || '';
        if (!iconUrl) throw new Error('Icon upload failed');
      }

      const payload = {
        type: form.type,
        label: form.label.trim(),
        url: form.url.trim(),
        iconType: form.iconType,
        iconUrl: form.iconType === 'image' ? iconUrl : '',      // favicon derived server-side
        iconKey: form.iconType === 'icon' ? form.iconKey : '',
      };

      if (editing) {
        const res = await api.put(`/toolbar-actions/${editing.id}`, payload);
        setActions(prev => prev.map(a => (a.id === editing.id ? res.data.action : a)));
      } else {
        const res = await api.post('/toolbar-actions', payload);
        setActions(prev => [...prev, res.data.action]);
      }
      setModalOpen(false);
    } catch (err) {
      console.error('[CustomToolbarActions] save failed:', err);
      setError(err.response?.data?.error || 'Could not save. Check the URL and try again.');
    } finally {
      setSaving(false);
    }
  };

  // Live preview object mirrors what the server will store
  const previewAction = {
    type: form.type,
    url: form.url,
    iconType: form.iconType,
    iconKey: form.iconKey,
    iconUrl: form.iconType === 'image' ? form.iconUrl
      : form.iconType === 'favicon' ? faviconFor(form.url)
      : '',
    emoji: form.emoji,
  };

  // Per-action options menu, portalled to <body> so the toolbar scroll can't clip it.
  const menuAction = openMenuId ? actions.find(a => a.id === openMenuId) : null;
  const menuIdx = menuAction ? actions.findIndex(a => a.id === openMenuId) : -1;
  const menu = menuAction ? ReactDOM.createPortal(
    <Menu style={{ top: menuPos.top, left: menuPos.left }} onClick={(e) => e.stopPropagation()}>
      <MenuItem onClick={() => openEdit(menuAction)}>Edit</MenuItem>
      <MenuItem disabled={menuIdx <= 0} onClick={() => move(menuAction, -1)}>Move up</MenuItem>
      <MenuItem disabled={menuIdx === actions.length - 1} onClick={() => move(menuAction, 1)}>Move down</MenuItem>
      <MenuItem className="danger" onClick={() => remove(menuAction)}>Delete</MenuItem>
    </Menu>,
    document.body
  ) : null;

  const modal = modalOpen ? ReactDOM.createPortal(
    <Overlay onClick={() => !saving && setModalOpen(false)}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <h3>{editing ? 'Edit action' : 'Add a toolbar action'}</h3>
        <p className="sub">A quick-launch button for one of your own tools or websites.</p>

        <label>What does it open?</label>
        <TypeToggle>
          <TypeOption
            $active={form.type === 'link'}
            onClick={() => setForm(prev => ({ ...prev, type: 'link', iconType: prev.iconType === 'favicon' ? 'favicon' : prev.iconType }))}
          >
            Website / web tool
            <small>Opens in a new tab</small>
          </TypeOption>
          <TypeOption
            $active={form.type === 'app'}
            onClick={() => setForm(prev => ({ ...prev, type: 'app', iconType: prev.iconType === 'favicon' ? 'icon' : prev.iconType }))}
          >
            App link
            <small>Zoom, Slack, mail…</small>
          </TypeOption>
        </TypeToggle>

        <label>Name</label>
        <Input
          placeholder="e.g. My CRM, Zoom, Company drive"
          value={form.label}
          maxLength={60}
          onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))}
        />

        {form.type === 'app' && (
          <>
            <label>Pick an app</label>
            <Hint style={{ marginTop: 0, marginBottom: 8 }}>
              Opens the app if it’s installed on this computer. For security, a browser
              can’t launch a program by file path (no “Browse to .exe”) — it can only open
              apps that registered a link. Office apps (Word/Excel/…) aren’t here because
              their link needs a document, not a blank launch.
            </Hint>
            <AppGrid>
              {APP_PRESETS.map(app => {
                const AppIcon = ICONS[app.icon] || ICONS.link;
                return (
                  <AppChoice
                    key={app.name}
                    type="button"
                    $active={form.url === app.url}
                    onClick={() => setForm(prev => ({
                      ...prev,
                      url: app.url,
                      label: prev.label.trim() ? prev.label : app.name,
                      iconType: prev.iconType === 'image' ? 'image' : 'icon',
                      iconKey: prev.iconType === 'image' ? prev.iconKey : app.icon,
                    }))}
                  >
                    <AppIcon className="ico" /> {app.name}
                  </AppChoice>
                );
              })}
            </AppGrid>
          </>
        )}

        <label>
          {form.type === 'link' ? 'Website address' : 'Or enter a custom app link (advanced)'}
        </label>
        <Input
          placeholder={form.type === 'link' ? 'https://…' : 'e.g. zoommtg://  ·  slack://  ·  mailto:  ·  tel:'}
          value={form.url}
          onChange={(e) => setForm(prev => ({ ...prev, url: e.target.value }))}
        />
        <Hint>
          {form.type === 'link'
            ? 'Paste the full web address including https://'
            : 'Opens an installed app via its link (protocol). A browser can’t launch a raw .exe or browse to a file path, so if the app isn’t installed / has no handler, the button simply does nothing. Examples: zoommtg://, slack://, ms-word:, mailto:, tel:'}
        </Hint>

        <label>Icon</label>
        <IconRow>
          {form.type === 'link' && (
            <IconChoice $active={form.iconType === 'favicon'} onClick={() => setForm(prev => ({ ...prev, iconType: 'favicon' }))}>
              Auto (site icon)
            </IconChoice>
          )}
          <IconChoice $active={form.iconType === 'icon'} onClick={() => setForm(prev => ({ ...prev, iconType: 'icon' }))}>
            Icon
          </IconChoice>
          <IconChoice $active={form.iconType === 'image'} onClick={() => fileInputRef.current?.click()}>
            Upload image
          </IconChoice>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            style={{ display: 'none' }}
            onChange={onPickFile}
          />
        </IconRow>
        {form.iconType === 'icon' && (
          <IconPicker>
            {ICON_PRESETS.map(key => {
              const Glyph = ICONS[key];
              return (
                <IconBtn key={key} type="button" $active={form.iconKey === key} onClick={() => setForm(prev => ({ ...prev, iconKey: key }))}>
                  <Glyph />
                </IconBtn>
              );
            })}
          </IconPicker>
        )}

        <PreviewRow>
          <PreviewBtn>{renderIcon(previewAction)}</PreviewBtn>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{form.label || 'Button name'}</div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.45)', wordBreak: 'break-all' }}>{form.url || '…'}</div>
          </div>
        </PreviewRow>

        {error && <ErrorText>{error}</ErrorText>}

        <Footer>
          <CancelBtn onClick={() => setModalOpen(false)} disabled={saving}>Cancel</CancelBtn>
          <SaveBtn onClick={save} disabled={!canSave}>{saving ? 'Saving…' : editing ? 'Save' : 'Add'}</SaveBtn>
        </Footer>
      </Modal>
    </Overlay>,
    document.body
  ) : null;

  return (
    <Section>
      <Divider />
      <AddButton onClick={openAdd} title="Add a tool / link">
        +
        <Tooltip>Add a tool or link</Tooltip>
      </AddButton>

      {actions.length > 0 && (
        <ActionsList>
          {actions.map((action, i) => (
            <ActionWrap key={action.id}>
              <SquareButton onClick={() => launch(action)} title={action.label}>
                {renderIcon(action)}
                <Tooltip>{action.label}</Tooltip>
              </SquareButton>
              <Kebab
                className="kebab"
                onClick={(e) => {
                  e.stopPropagation();
                  if (openMenuId === action.id) { setOpenMenuId(null); return; }
                  const r = e.currentTarget.getBoundingClientRect();
                  // fly out to the LEFT of the kebab (toolbar sits at the screen's right edge)
                  setMenuPos({ top: Math.min(r.top, window.innerHeight - 170), left: Math.max(8, r.left - 150) });
                  setOpenMenuId(action.id);
                }}
                title="Options"
              >
                ⋯
              </Kebab>
            </ActionWrap>
          ))}
        </ActionsList>
      )}

      {menu}
      {modal}
    </Section>
  );
};

export default CustomToolbarActions;
